import React from 'react';
import { Eye, Trash2, Clock, TrendingUp, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import type { AnalysisSummary, Verdict } from '../../types/acquisitionAnalysis';
import { Spinner } from '../common/Spinner';

interface AnalysisHistoryProps {
  analyses: AnalysisSummary[];
  isLoading: boolean;
  error: Error | null;
  onViewAnalysis: (analysisId: string) => void;
  onDeleteAnalysis: (analysisId: string) => void;
}

export const AnalysisHistory: React.FC<AnalysisHistoryProps> = ({
  analyses,
  isLoading,
  error,
  onViewAnalysis,
  onDeleteAnalysis,
}) => {
  const getVerdictBadge = (verdict: Verdict) => {
    const configs = {
      STRONG_BUY: {
        icon: CheckCircle,
        text: 'Strong Buy',
        className: 'bg-green-500/20 text-green-300 border-green-500/30',
      },
      BUY: {
        icon: TrendingUp,
        text: 'Buy',
        className: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      },
      CAUTION: {
        icon: AlertTriangle,
        text: 'Caution',
        className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      },
      AVOID: {
        icon: TrendingDown,
        text: 'Avoid',
        className: 'bg-red-500/20 text-red-300 border-red-500/30',
      },
    };

    const config = configs[verdict] || configs.CAUTION; // Default to CAUTION if verdict undefined
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (error) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-300">Error Loading History</h3>
            <p className="mt-1 text-sm text-red-400">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-700/50">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Recent Analyses
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          View and manage past trail book valuations
        </p>
      </div>

      {isLoading ? (
        <div className="p-8 text-center">
          <Spinner size="md" label="Loading history..." />
        </div>
      ) : analyses.length === 0 ? (
        <div className="p-8 text-center">
          <Clock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No analyses yet</p>
          <p className="text-xs text-slate-500 mt-1">
            Start your first analysis to see it here
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-700/50">
          {analyses.map((analysis) => (
            <div
              key={analysis.analysisId}
              className="p-4 hover:bg-slate-700/30 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-white mb-1">
                    {analysis.targetName}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {formatDate(analysis.date)}
                  </p>
                </div>
                <div>{getVerdictBadge(analysis.verdict)}</div>
              </div>

              <div className="mb-3">
                <p className="text-xs text-slate-500 mb-0.5">Annual Trail Income</p>
                <p className="text-sm font-semibold text-indigo-400">
                  {formatCurrency(analysis.annualTrailIncome)}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onViewAnalysis(analysis.analysisId)}
                  className="flex-1 px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                >
                  <Eye className="w-3.5 h-3.5" />
                  View
                </button>
                <button
                  onClick={() => onDeleteAnalysis(analysis.analysisId)}
                  className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded text-xs font-medium transition-colors flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
